import { CSSProperties, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Icon } from '@iconify/react'
import { isCancelledError } from 'react-query'
import useIsMobile from '../../../../utils/detectDevice'
import { HStack } from '../../../../utils/Stack'
import {
  AllAccountOfUserQuery,
  useAllAccountOfUserQuery,
  useSwitchAccountMutation,
} from '../../../../generated/graphql'
import {
  getApiErrorMessage,
  graphqlRequestClient,
  isNoInvestorFoundError,
  isUnauthorizedError,
} from '../../../../queries/client'
import {
  clearAccountScopedSession,
  getAccounts,
  getActiveAccount,
} from '../../../../utils/SessionStorageUtils'
import Toast, { ToastType } from '../../../../utils/toast'

type AccountOption = { id: string; name: string }

function TruncatedText({
  text,
  className,
  style,
}: {
  text: string
  className: string
  style?: CSSProperties
}) {
  const textRef = useRef<HTMLSpanElement>(null)
  const [isTruncated, setIsTruncated] = useState(false)

  useLayoutEffect(() => {
    const element = textRef.current
    if (!element) return

    const updateTruncation = () => {
      setIsTruncated(element.scrollWidth > element.clientWidth)
    }

    updateTruncation()
    window.addEventListener('resize', updateTruncation)

    const resizeObserver =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateTruncation) : undefined
    resizeObserver?.observe(element)

    return () => {
      window.removeEventListener('resize', updateTruncation)
      resizeObserver?.disconnect()
    }
  }, [text])

  return (
    <span ref={textRef} className={className} style={style} title={isTruncated ? text : undefined}>
      {text}
    </span>
  )
}

export function AccountSwitcher() {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const isSwitchingRef = useRef(false)
  const { isMobile } = useIsMobile(1024)

  const activeAccount = getActiveAccount()
  const allAccountsQuery = useAllAccountOfUserQuery<AllAccountOfUserQuery, Error>(
    graphqlRequestClient,
    {},
    {}
  )
  const accounts: AccountOption[] = allAccountsQuery.data?.getAllAccountOfUser ?? getAccounts()

  const switchAccountMutation = useSwitchAccountMutation<Error>(graphqlRequestClient, {
    onError: (error: any, variables) => {
      if (isNoInvestorFoundError(error)) {
        const accountName = accounts.find(
          (account) => String(account.id) === String(variables.input.accountId)
        )?.name

        Toast(
          accountName
            ? `No investor is associated with "${accountName}". Please add an investor to this account and try again.`
            : 'No investor is associated with the selected account. Please add an investor and try again.',
          ToastType.error
        )
        return
      }

      if (isUnauthorizedError(error)) return
      if (isCancelledError(error)) return

      Toast(getApiErrorMessage(error), ToastType.error)
    },
    onSettled: () => {
      isSwitchingRef.current = false
    },
  })

  function handleSwitchAccount(account: AccountOption) {
    setIsOpen(false)
    if (String(account.id) === String(activeAccount?.id)) return
    if (isSwitchingRef.current || switchAccountMutation.isLoading) return

    isSwitchingRef.current = true
    switchAccountMutation.mutate(
      { input: { accountId: account.id as any } },
      {
        onSuccess: () => {
          // Wipe all account-scoped session state (userInvestors, userCompanies,
          // accounts, activeAccount, roles, datasets, ...) so nothing from the old
          // account leaks into the UI, then optimistically seed the switched-to
          // account so it shows immediately. Clearing userRoles re-enables the
          // boot-time user-context query, which rebuilds this state on reload.
          clearAccountScopedSession({ id: String(account.id), name: account.name })
          window.location.reload()
        },
      }
    )
  }

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Nothing to switch between — keep the top bar clean for single-account users.
  if (accounts.length <= 1) return null

  const activeName =
    activeAccount?.name ??
    accounts.find((account) => String(account.id) === String(activeAccount?.id))?.name ??
    accounts[0]?.name ??
    'Account'

  return (
    <div className='position-relative' ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`border-0 border-top-bar-bottom h-55px ${
          isMobile ? 'w-125px' : 'w-175px'
        } px-3 text-gray-700 text-hover-primary ${
          switchAccountMutation.isLoading ? 'opacity-50 cursor-not-allowed' : ''
        }`}
        type='button'
        disabled={switchAccountMutation.isLoading}
        aria-label='Switch account'
        aria-haspopup='listbox'
        aria-expanded={isOpen}
        aria-busy={switchAccountMutation.isLoading}
      >
        <HStack className='d-flex justify-content-between align-items-center gap-2'>
          <TruncatedText
            text={activeName}
            className='fw-bold fs-6 text-truncate'
            style={{ maxWidth: isMobile ? 82 : 132 }}
          />

          {switchAccountMutation.isLoading ? (
            <span className='spinner-border spinner-border-sm flex-shrink-0' role='status' />
          ) : (
            <svg
              className={`w-15px h-15px flex-shrink-0 transition-transform ${
                isOpen ? 'rotate-180' : 'rotate-0'
              }`}
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth='2'
                d='M19 9l-7 7-7-7'
              />
            </svg>
          )}
        </HStack>
      </button>

      {isOpen && (
        <div
          className={`position-absolute start-50 translate-middle-x bg-white border border-gray-200 rounded shadow-lg z-10 p-2 ${
            isMobile ? 'mt-8' : 'mt-2'
          }`}
          style={{ top: '100%', width: isMobile ? 175 : 200 }}
        >
          <div className='px-2 pb-2 fs-8 fw-semibold text-muted text-uppercase'>Select Account</div>

          <div
            className='mh-300px overflow-auto scroll-smooth'
            style={{ overflowY: 'auto', overflowX: 'hidden', scrollbarWidth: 'none' }}
            role='listbox'
            aria-label='Account options'
          >
            {accounts.map((account, index) => {
              const isActive = String(account.id) === String(activeAccount?.id)
              return (
                <button
                  key={account.id}
                  type='button'
                  disabled={switchAccountMutation.isLoading}
                  role='option'
                  aria-selected={isActive}
                  className={`position-relative w-100 border-0 rounded py-2 px-2 d-flex align-items-center justify-content-center ${
                    isActive
                      ? 'bg-light-primary text-primary fw-bold'
                      : 'bg-transparent bg-hover-gray-light text-gray-700'
                  } ${index !== 0 ? 'mt-1' : ''}`}
                  onClick={() => handleSwitchAccount(account)}
                >
                  <TruncatedText
                    text={account.name}
                    className='w-100 px-4 fs-7 text-center text-truncate'
                  />
                  {isActive && (
                    <Icon
                      icon='mdi:check'
                      className='position-absolute end-0 me-1 text-primary'
                      width={16}
                      height={16}
                    />
                  )}
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
